// ** react imports
import React, { useState } from 'react';

// ** toaster imports
import { toast } from 'react-toastify';

// ** hooks
import { useDeleteAnnouncement } from '../../../hooks/apis/useAnnouncement';

// ** components
import ModalBasic from '../../../components/ModalBasic';
import Loading from '../../../components/common/Loading';
import AnnouncementTableItem from './AnnouncementTableItem';

const AnnouncementTable = props => {
  // props
  const { data, isLoading, currentSortBy, onSort, refetch } = props;

  // states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // handlers
  const handleOpenDeleteModal = item => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const { deleteAnnouncement, isDeleteAnnouncementLoading } =
    useDeleteAnnouncement();

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    const response = await deleteAnnouncement(itemToDelete._id);
    if (response.data.success) {
      refetch();
      setDeleteModalOpen(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (currentSortBy !== field) {
      return (
        <svg
          className="w-3 h-3 ml-1 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return currentSortBy === field && currentSortBy === 'asc' ? (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handleSortClick = field => {
    if (onSort) {
      onSort(field);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 h-[calc(70vh)] shadow-xs rounded-xl relative overflow-y-auto">
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
            <Loading />
          </div>
        </div>
      )}

      <div>
        <header className="px-4 sm:px-5 py-4 bg-white dark:bg-gray-800 sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              All Announcements{' '}
              <span className="text-gray-400 dark:text-gray-500 font-medium">
                {data?.total || 0}
              </span>
            </h2>
          </div>
        </header>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700/60">
            <thead className="sticky top-0 z-10 text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/20 border-t border-b border-gray-100 dark:border-gray-700/60">
              <tr>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[150px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('title')}
                    aria-label="Sort by title"
                  >
                    Title
                    <SortIcon field="title" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('category')}
                    aria-label="Sort by category"
                  >
                    Category
                    <SortIcon field="category" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('urgency')}
                    aria-label="Sort by urgency"
                  >
                    Urgency
                    <SortIcon field="urgency" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px] text-center"
                >
                  <button
                    className="font-semibold text-center whitespace-nowrap uppercase flex items-center justify-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('isActive')}
                    aria-label="Sort by active status"
                  >
                    Is Active
                    <SortIcon field="isActive" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[150px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('publishDate')}
                    aria-label="Sort by publish date"
                  >
                    Publish Date
                    <SortIcon field="publishDate" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[150px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('expiryDate')}
                    aria-label="Sort by expiry date"
                  >
                    Expiry Date
                    <SortIcon field="expiryDate" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[80px] max-w-[100px] text-center"
                >
                  <div className="font-semibold text-center">Actions</div>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-700/60">
              {data?.data && data.data.length > 0 ? (
                data.data.map(announcement => (
                  <AnnouncementTableItem
                    key={announcement._id}
                    data={announcement}
                    viewMode="table"
                    onOpenDeleteModal={handleOpenDeleteModal}
                  />
                ))
              ) : (
                <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <td colSpan="8" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="22"
                          viewBox="0 0 22 22"
                          fill="currentColor"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M9 17C9 17 16 18 19 21H20C20.5523 21 21 20.5523 21 20V13.937C21.8626 13.715 22.5 12.9319 22.5 12C22.5 11.0681 21.8626 10.285 21 10.063V4C21 3.44772 20.5523 3 20 3H19C16 6 9 7 9 7H5C3.89543 7 3 7.89543 3 9V15C3 16.1046 3.89543 17 5 17H6L7 22H9V17ZM11 8.6612C11.6833 8.5146 12.5275 8.31193 13.4393 8.04373C15.1175 7.55014 17.25 6.77262 19 5.57458V18.4254C17.25 17.2274 15.1175 16.4499 13.4393 15.9563C12.5275 15.6881 11.6833 15.4854 11 15.3388V8.6612ZM5 9H9V15H5V9Z"></path>
                          </svg>
                        </svg>
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        No Announcements found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Try adjusting your filters or search criteria to find
                        what you&apos;re looking for.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        id={`delete-modal-${itemToDelete?._id}`}
        modalOpen={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        title="Delete Announcement"
      >
        <div className="px-5 pt-4 pb-1">
          <div className="text-sm mb-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete the announcement{' '}
              <strong>{itemToDelete?.title}</strong>?
            </p>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              This action cannot be undone. The announcement will be permanently
              removed from the system.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60 flex justify-end space-x-3">
          <button
            className="btn px-4 py-2 rounded-lg border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => setDeleteModalOpen(false)}
            disabled={isDeleteAnnouncementLoading}
          >
            Cancel
          </button>
          <button
            className="btn px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center"
            onClick={handleDeleteConfirm}
            disabled={isDeleteAnnouncementLoading}
          >
            {isDeleteAnnouncementLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </ModalBasic>
    </div>
  );
};

export default AnnouncementTable;
